import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { TenantCreateModal } from '../../components/TenantCreateModal';
import { api } from '../../services/api';
import { Tenant } from '../../types';

interface TenantListResponse {
  tenants: Tenant[];
  totalCount: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState({ totalTenants: 0, activeTenants: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    async function loadStats() {
      try {
        const data = await api.get<TenantListResponse>('/admin/tenants');
        const active = data.tenants.filter((t) => t.status === 'active').length;
        setStats({
          totalTenants: data.totalCount,
          activeTenants: active,
        });
      } catch (err) {
        console.error('Failed to load stats:', err);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  const handleTenantCreated = (tenant: Tenant) => {
    setStats({
      totalTenants: stats.totalTenants + 1,
      activeTenants: tenant.status === 'active' ? stats.activeTenants + 1 : stats.activeTenants,
    });
  };

  return (
    <Layout>
      <h1 style={{ marginBottom: '24px' }}>SPEDflow Admin Dashboard</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        <DashboardCard
          title="Total Tenants"
          value={loading ? '...' : stats.totalTenants.toString()}
          link="/admin/tenants"
          linkText="View All Tenants"
        />
        <DashboardCard
          title="Active Tenants"
          value={loading ? '...' : stats.activeTenants.toString()}
          color="#4caf50"
        />
        <DashboardCard
          title="System Health"
          value="OK"
          color="#2196f3"
          subtitle="All services operational"
        />
        <ActionCard
          title="Add Tenant"
          description="Create a new tenant organization"
          buttonText="+ New Tenant"
          onClick={() => setShowCreateModal(true)}
        />
      </div>

      <TenantCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleTenantCreated}
      />
    </Layout>
  );
}

interface DashboardCardProps {
  title: string;
  value: string;
  color?: string;
  link?: string;
  linkText?: string;
  subtitle?: string;
}

function DashboardCard({ title, value, color = '#1a237e', link, linkText, subtitle }: DashboardCardProps) {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>{title}</h3>
      <p style={{ fontSize: '36px', fontWeight: 'bold', color, margin: '0 0 8px' }}>{value}</p>
      {subtitle && <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>{subtitle}</p>}
      {link && (
        <Link to={link} style={{ color: '#1a237e', fontSize: '14px' }}>
          {linkText} →
        </Link>
      )}
    </div>
  );
}


interface ActionCardProps {
  title: string;
  description: string;
  buttonText: string;
  onClick: () => void;
}

function ActionCard({ title, description, buttonText, onClick }: ActionCardProps) {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}>
      <div>
        <h3 style={{ color: '#1a237e', fontSize: '16px', marginBottom: '8px' }}>{title}</h3>
        <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>{description}</p>
      </div>
      <button
        onClick={onClick}
        style={{
          marginTop: '16px',
          padding: '10px 20px',
          backgroundColor: '#1a237e',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
        }}
      >
        {buttonText}
      </button>
    </div>
  );
}
